// The browser's side of the auth library seam. Every identity call the
// screens make goes through here: the library answers under `/api/auth`
// and reports a refusal as `{ message, code }`, where the factory's own
// routes say `{ error }`; one reader turns both into an AuthApiError.
// See ADR-0042 §1.

/** The path prefix the auth library answers under. */
export const AUTH_BASE = "/api/auth";

/**
 * A refused auth call: the HTTP status, the server's message and its
 * refusal code when it sent one. A 429 is reworded as a rate limit.
 */
export class AuthApiError extends Error {
  status: number;
  code: string | null;
  constructor(status: number, message: string, code: string | null) {
    super(message);
    this.name = "AuthApiError";
    this.status = status;
    this.code = code;
  }
}

async function readError(res: Response, fallback: string): Promise<AuthApiError> {
  let message = fallback;
  let code: string | null = null;
  try {
    const body = (await res.json()) as { message?: string; error?: string; code?: string };
    if (body?.message) message = body.message;
    else if (body?.error) message = body.error;
    if (body?.code) code = body.code;
  } catch {
    /* body was empty or not JSON */
  }
  if (res.status === 429) {
    message = "Too many attempts. Wait a moment and try again.";
    code = code ?? "RATE_LIMITED";
  }
  return new AuthApiError(res.status, message, code);
}

/**
 * Posts a JSON body to a path under `/api/auth` and resolves with the
 * parsed body, or null when the body is empty. Rejects with AuthApiError
 * carrying the server's message and code.
 */
export async function authPost<T = unknown>(
  path: string,
  body: Record<string, unknown>,
): Promise<T> {
  const res = await fetch(`${AUTH_BASE}${path}`, {
    method: "POST",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw await readError(res, `${path} → ${res.status}`);
  const text = await res.text();
  return (text ? JSON.parse(text) : null) as T;
}

/**
 * Gets a path under `/api/auth` and resolves with the parsed JSON body.
 * Rejects with AuthApiError carrying the server's message and code.
 */
export async function authGet<T = unknown>(path: string): Promise<T> {
  const res = await fetch(`${AUTH_BASE}${path}`, {
    credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
  });
  if (!res.ok) throw await readError(res, `${path} → ${res.status}`);
  return (await res.json()) as T;
}

/** The message a screen shows for a caught error: the server's words when it sent any, else `fallback`. */
export function errorMessage(e: unknown, fallback = "could not reach the server"): string {
  if (e instanceof AuthApiError) return e.message;
  if (e instanceof Error && e.message) return e.message;
  return fallback;
}

/**
 * Words a refusal code from a verification, reset or invitation link as
 * one sentence telling the person what to do next. Unknown codes get the
 * generic "did not work" sentence.
 */
export function staleLinkMessage(code: string | null): string {
  switch (code) {
    case "TOKEN_EXPIRED":
      return "This link has expired. Ask for a new one below.";
    case "INVALID_TOKEN":
      return "This link is no longer valid — it may already have been used. Ask for a new one below.";
    case "USER_NOT_FOUND":
      return "The account this link belongs to no longer exists.";
    case "INVITATION_NOT_FOUND":
      return "This invitation is no longer valid — it may have been cancelled or already used. Ask the person who invited you for a new one.";
    case "INVITATION_EXPIRED":
      return "This invitation has expired. Ask the person who invited you for a new one.";
    case "YOU_ARE_NOT_THE_RECIPIENT_OF_THE_INVITATION":
      return "This invitation was sent to a different email address, so you are not the recipient. Sign out, then sign in as the invited address.";
    case "USER_IS_ALREADY_A_MEMBER_OF_THIS_ORGANIZATION":
      return "You are already a member of this Organisation.";
    default:
      return "This link did not work. Ask for a new one below.";
  }
}

// The refusal codes the accept-invitation page names; any other refusal
// shows the server's own words.
const INVITATION_CODES = new Set([
  "INVITATION_NOT_FOUND",
  "INVITATION_EXPIRED",
  "YOU_ARE_NOT_THE_RECIPIENT_OF_THE_INVITATION",
  "USER_IS_ALREADY_A_MEMBER_OF_THIS_ORGANIZATION",
]);

/**
 * The message the accept-invitation page shows for a refused accept: the
 * stale-link sentence for a known invitation code, the server's own words
 * for anything else.
 */
export function invitationRefusalMessage(e: unknown): string {
  if (e instanceof AuthApiError && e.code && INVITATION_CODES.has(e.code)) {
    return staleLinkMessage(e.code);
  }
  return errorMessage(e);
}
